'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { needsDataRequirement } from 'src/utils/routeDataRequirements';
import useVelocityStore, {
	VelocityStore,
} from 'src/stores/VelocityStore/useVelocityStore';
import useAppParams from './useAppParams';

const DEFAULT_STATUS = {
	velocityStatus: {
		status: 'green',
		message: '',
	} as VelocityStore['velocityStatus']['mainStatus'],
};

/**
 * Get the velocity status from aws s3 bucket. Should be in the form `{status} | {message}` where status can be green|amber|red|'' and message can be any string.
 *
 * e.g:
 * - 'green | Things are back to normal'
 * - 'amber | The solana network is experiencing congestion'
 * - 'false'|'none'|'' (no status to display)
 * @returns
 */
const getStatus = (params: { status: string; message: string; alert: any }) => {
	const velocityStatus = {
		status: params?.status ?? '',
		message: params?.message ?? '',
	} as VelocityStore['velocityStatus']['mainStatus'];

	return { velocityStatus };
};

const useSyncVelocityStatus = () => {
	const pathname = usePathname();
	const appParams = useAppParams();

	const setState = useVelocityStore((s) => s.set);

	// Only check velocity status on trading and portfolio pages where status matters
	const enabled = pathname
		? needsDataRequirement(pathname, 'trading') ||
			needsDataRequirement(pathname, 'portfolio')
		: false;

	useEffect(() => {
		if (!appParams || !enabled) return;

		// Check status from s3
		const { velocityStatus } = getStatus(appParams);

		if (velocityStatus.status) {
			setState((s) => {
				s.velocityStatus.mainStatus = {
					...s.velocityStatus.mainStatus,
					...velocityStatus,
				};
			});
			return;
		}

		setState((s) => {
			s.velocityStatus.mainStatus = {
				...s.velocityStatus.mainStatus,
				...DEFAULT_STATUS.velocityStatus,
			};
		});
	}, [appParams]);
};

export default useSyncVelocityStatus;
